import React, { useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { getClassNames } from 'js/utils/classNames.util';
import buildClassName from 'js/utils/classNameBuilder.util';
import DropDownIcon from 'js/components/common/Fields/Icons/DropDownIcon';

import * as helpers from '../../helpers';

import style from './Picker.scss';
import selectStyle from '../../../Select/Select.scss';

export const Picker = (props) => {
    const { type, isBorderShown = false, disabled, range, readOnly, parse, setValue, onMouseDown, ...input } = props;
    const { onChange, value } = input;
    const translate = useTranslations('playtech.mobile.system.');
    const picker = useMemo(() => helpers.pickerStates[type]({ value, range, translate }),
        [type, value, range, translate]);
    const pickerClassName = useMemo(() => getClassNames([
        selectStyle.content,
        style.picker,
        style[type],
        isBorderShown ? style.showBorder : '',
        disabled || readOnly ? style.disabled : '',
        disabled || readOnly ? selectStyle.disabled : ''
    ]), [type, isBorderShown, disabled, readOnly]);
    const selectClassName = useMemo(() => getClassNames([
        selectStyle.select,
        picker.value == null || picker.value === '' ? selectStyle.empty : ''
    ]), [picker.value]);

    const handleChange = useCallback((event) => {
        onChange({
            ...value,
            [type]: Number(event.target.value)
        });
    }, [onChange, value, type]);

    const preventClick = useCallback(event => event.preventDefault(), []);

    useEffect(() => {
        if (!value[type] || !picker.options.length || !setValue) {
            return;
        }

        const isValueMissing = picker.options.every(item => item.value.toString() !== picker.value.toString());

        if (isValueMissing) {
            const { year, month, day } = value;
            const newVal = {
                [type]: picker.options[0].value
            };

            setValue(parse({
                year,
                month,
                day,
                ...newVal
            }));
        }
    }, [value, picker, type, parse, setValue]);

    return (
        <div className={pickerClassName}>
            <select
                {...input}
                disabled={disabled}
                value={picker.value}
                onChange={handleChange}
                className={selectClassName}
                tabIndex={readOnly ? -1 : 0}
                aria-label={picker.placeholder || translate('select.aria-label')}
                onMouseDown={readOnly ? preventClick : onMouseDown}
            >
                    <option value="" disabled className="hidden">
                        {picker.placeholder}
                    </option>
                    {picker.options.map(item => (
                        <option
                            key={item.value}
                            value={item.value}
                        >
                            {item.label || item.value}
                        </option>
                    ))}
            </select>
            <DropDownIcon className={buildClassName(['dropDownIcon'], style)} disabled={disabled || readOnly} />
        </div>
    );
};
